import File from '../Model/File'
import { getClosestElement, getElementChild, getTemplate } from '../Utility/Dom'
import { IS_DRAG } from '../Utility/State'

class FormFileService {
  private fileInputs: NodeListOf<HTMLInputElement>

  constructor () {
    this.fileInputs = document.querySelectorAll('[data-js="form-element"][type="file"]')
  }

  public init (): void {
    if (!window.File) return
    if (!window.FileList) return
    if (!window.FileReader) return

    this.addEvents()
  }

  private addEvents (): void {
    this.fileInputs.forEach((fileInput: HTMLInputElement): void => {
      fileInput.addEventListener('change', (e) => this.onSelect(e, fileInput), false)
      fileInput.addEventListener('dragover', (e) => this.onDrag(e), false)
      fileInput.addEventListener('dragleave', (e) => this.onDrag(e), false)
      fileInput.addEventListener('drop', (e) => this.onSelect(e, fileInput), false)
    })
  }

  private addFileInfos (file: File, fileList: HTMLElement): void {
    const fileTemplate = getTemplate('file-template')
    const fileInfo = getElementChild(fileTemplate, 'file-info')
    if (fileInfo === null) return

    const fileName = getElementChild(fileInfo, 'file-name')
    const fileSize = getElementChild(fileInfo, 'file-size')
    const fileIcon = getElementChild(fileInfo, 'file-icon')

    if (fileName !== null) {
      (fileName as HTMLElement).innerText = file.name
    }

    if (fileSize !== null) {
      (fileSize as HTMLElement).innerText = file.sizeFormatted
    }

    if (fileIcon !== null) {
      fileIcon.className += file.icon
    }

    fileList.appendChild(fileInfo)
  }

  private getAllowedMimeTypes (fileInput: HTMLInputElement): [] | string[] {
    const accept = fileInput.getAttribute('accept')
    if (accept === null) return []

    return accept.split(',').map(type => type.trim())
  }

  private isValidFileType (file, allowedMimeTypes) {
    return allowedMimeTypes.some((type) => {
      return type.startsWith('.') ? file.name.endsWith(type) : file.type === type
    })
  }

  private onDrag (event): void {
    event.stopPropagation()
    event.preventDefault()

    if (event.type === 'dragover') {
      event.target.classList.add(IS_DRAG)
    } else {
      event.target.classList.remove(IS_DRAG)
    }
  }

  private onSelect (event, fileInput): void {
    this.onDrag(event)
    let files

    if (event?.dataTransfer?.files) {
      event.target.files = event.dataTransfer.files
    }

    if (event.target.files) {
      files = event.target.files
    }

    if (files.length === 0) {
      return
    }

    files = [...files]

    const allowedMimeTypes = this.getAllowedMimeTypes(fileInput)
    if (allowedMimeTypes.length > 0) {
      const validFiles = files.filter((file) => this.isValidFileType(file, allowedMimeTypes))

      if (validFiles.length < files.length) {
        alert(fileInput.getAttribute('data-message-files-format') || 'Some files have been removed because their format is not supported.')
      }

      if (validFiles.length === 0) {
        alert(fileInput.getAttribute('data-message-file-format') || 'File format not supported.')
        return
      }

      const dataTransfer = new DataTransfer()
      validFiles.forEach((file) => dataTransfer.items.add(file))
      fileInput.files = dataTransfer.files
      files = validFiles
    }

    const fromGroup = getClosestElement(event.target, 'form-group')
    const fileList = getElementChild(fromGroup, 'file-list')
    if (fileList === null) return
    fileList.innerHTML = ''

    files.forEach((fileReference): void => {
      const file = new File(fileReference)

      this.addFileInfos(file, fileList)
    })
  }
}

export default FormFileService
